import { Box, Typography, useTheme } from '@mui/material';
import { tokens } from '../../../global/theme/tokens';
import { useTranslation } from 'react-i18next';
import CardGrid from '../../../components/cards/layout/CardGrid';
import { API_DELETE, DBRequest, useConfig } from '../../../api/api';
import FilePreviewCard from '../../../components/cards/FilePreviewCard';
import SpeedDialMenu from '../../../components/menu/SpeedDialMenu';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import InfoCard from '../../../components/cards/InfoCard';
import MoneyCard from '../../../components/cards/MoneyCard';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TextCard from '../../../components/cards/TextCard';
import MoneyLabel from '../../../components/label/MoneyLabel';
import DateLabel from '../../../components/label/DateLabel';
import FileCard from '../../../components/cards/FilesCard';
import DownloadIcon from '@mui/icons-material/Download';
import SkeletonCard from '../../../components/cards/SkeletonCard';
import CardContainer from '../../../components/cards/layout/CardContainer';
import StyledTextContainer from '../../../components/preview/StyledTextContainer';
import RenderHTML from '../../../components/preview/RenderHTML';

export const ExpandPayroll = ({ row, onCreate, onDelete, onEdit }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  function handleDelete() {
    DBRequest({
      config,
      path: `payrolls/${row.id}`,
      method: API_DELETE(),
      onResponse: () => onDelete(row),
    });
  }

  const actions = [
    {
      icon: <EditIcon />,
      name: t('Edit'),
      onClick: () => onEdit(row),
    },
    { icon: <DeleteOutlineIcon />, name: t('Delete'), onClick: handleDelete },
  ];

  if (!row) return <PayrollSkeleton />;

  const infoRows = [
    {
      label: t('Month'),
      value: <DateLabel format="MM/YYYY" date={row.endDate} />,
    },
    row.incomeTax && {
      label: t('Income Tax'),
      value: <MoneyLabel money={row.incomeTax} />,
    },
    row.voluntarySocialBenefits && {
      label: t('Voluntary Social Benefits'),
      value: <MoneyLabel money={row.voluntarySocialBenefits} />,
    },
  ].filter(Boolean);

  const files = row.file
    ? [
        {
          label: t('Payroll'),
          url: row.file.temporaryUrl,
          icon: <DownloadIcon />,
        },
      ]
    : [];

  return (
    <CardGrid>
      <FilePreviewCard file={row.file} />
      <InfoCard
        span={2}
        customContent={infoRows.map((info, index) => (
          <Box key={index} className="flex flex-row gap-2 items-center px-2">
            <Typography variant="h6">{info.label}</Typography>
            <Typography sx={{ fontWeight: 500 }}>{info.value}</Typography>
          </Box>
        ))}
      />
      <FileCard files={files} />
      <MoneyCard
        amount={row.amount}
        Icon={AttachMoneyIcon}
        title={t('Salary')}
        subTitle={
          row.incomeBrutto ? (
            <Typography variant="h6">
              <b>
                <MoneyLabel money={row.incomeBrutto} />
              </b>{' '}
              {t('brutto')}
            </Typography>
          ) : null
        }
      />
      {row.comment && (
        <CardContainer span={4}>
          <StyledTextContainer
            backgroundColor="transparent"
            fullWidth
            color={colors.text}
          >
            <RenderHTML HTML={row.comment} />
          </StyledTextContainer>
        </CardContainer>
      )}
      <SpeedDialMenu actions={actions} />
    </CardGrid>
  );
};

const PayrollSkeleton = () => {
  return (
    <CardGrid>
      <SkeletonCard height={52} span={5} />
      <SkeletonCard height={191} />
      <SkeletonCard height={191} />
      <SkeletonCard height={191} />
      <SkeletonCard height={191} />
    </CardGrid>
  );
};
